import DOMPurify from 'dompurify';
// Blog.tsx — Co Marca Group — datos reales del backend
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPublicaciones, fetchDestacados, Publicacion } from '../../services/landing/blogService';
import { SAMPLE_BLOG_POSTS } from '../../data/sampleBlogPosts';
import BrandStripes from '../../components/common/BrandStripes';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary:       '#61257B',
    primaryDark:   '#451759',
    secondary:     '#C1348B',
    secondaryLight:'#FFD97C',
    accent:        '#FFD97C',
    accentDark:    '#EA8406',
};

const paletteColors = [colors.primary, colors.secondary, '#EA8406', '#8f1f64', '#5b21b6', colors.primaryDark];
const paletteGrads  = [
    `linear-gradient(145deg,${colors.primaryDark},${colors.primary})`,
    `linear-gradient(145deg,#61257B,${colors.secondary})`,
    `linear-gradient(145deg,#451759,${colors.secondary})`,
    `linear-gradient(145deg,#7a4800,${colors.accentDark})`,
    'linear-gradient(145deg,#3b0764,#5b21b6)',
    `linear-gradient(145deg,${colors.primaryDark},#6b0032)`,
];
const emojis = ['🚀','🤖','💰','🤝','🌐','📊','📈','🧠','✍️','💡','🎯','📌'];

// Funciones con 2 args (post + índice) — usadas en Blog.tsx
const postColor = (_: Publicacion, i: number) => paletteColors[i % paletteColors.length];
const postGrad  = (_: Publicacion, i: number) => paletteGrads[i % paletteGrads.length];
const postEmoji = (_: Publicacion, i: number) => emojis[i % emojis.length];
const initials  = (n: string) => n.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const fmtDate   = (d: string|null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('es-BO',{day:'numeric',month:'short',year:'numeric'}); } catch { return d; }
};

function useInView(threshold: number = 0.1): [React.RefObject<HTMLElement | null>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const r = ref.current.getBoundingClientRect();
            if (r.top < window.innerHeight && r.bottom > 0) { setInView(true); return; }
        }
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

// ── Featured Post ──
function FeaturedPost({ post, idx, onClick }: { post: Publicacion; idx: number; onClick: () => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.08);
    const [hov, setHov] = useState(false);
    const color = postColor(post, idx);
    const grad  = postGrad(post, idx);
    const emoji = postEmoji(post, idx);
    const imgSrc = post.url_imagen || post.imagen || null;

    return (
        <div ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transition: 'all .4s cubic-bezier(.16,1,.3,1)',
                transform: inView ? (hov ? 'translateY(-4px)' : 'translateY(0)') : 'translateY(32px)',
                opacity: inView ? 1 : 0,
                marginBottom: '28px',
                width: '100%',
            }}
        >
            <BorderGlow
                borderRadius={28}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                onClick={onClick}
                onMouseEnter={() => setHov(true)}
                onMouseLeave={() => setHov(false)}
                style={{ cursor: 'pointer', width: '100%' }}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
                    {/* Cover — imagen por encima del gradiente */}
                    <div style={{ flex: '0 0 480px', minHeight: '380px', position: 'relative', overflow: 'hidden', background: grad }}>
                        {/* Gradiente/emoji como fondo base */}
                        <div style={{ position: 'absolute', inset: 0, background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 0 }}>
                            <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '220px', height: '220px', background: 'rgba(255,255,255,.06)', borderRadius: '50%' }} />
                            <span style={{ fontSize: '6rem', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,.2))' }}>{emoji}</span>
                        </div>
                        {/* Imagen real encima del gradiente */}
                        {imgSrc && (
                            <img src={imgSrc} alt={post.titulo}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1, transition: 'transform .6s cubic-bezier(.16,1,.3,1)', transform: hov ? 'scale(1.05)' : 'scale(1)' }}
                                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        {/* Overlay oscuro para legibilidad de badges */}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,.35) 0%,transparent 50%)', zIndex: 2 }} />
                        <div style={{ position: 'absolute', bottom: '20px', left: '20px', display: 'flex', gap: '8px', zIndex: 3 }}>
                            <span style={{ background: 'rgba(255,255,255,.2)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,.3)', borderRadius: '8px', padding: '5px 14px', fontSize: '.72rem', fontWeight: '700', color: '#fff', letterSpacing: '.06em' }}>
                                {post.categoria?.nombre || 'General'}
                            </span>
                            <span style={{ background: 'rgba(0,0,0,.3)', backdropFilter: 'blur(8px)', borderRadius: '8px', padding: '5px 14px', fontSize: '.72rem', fontWeight: '700', color: 'rgba(255,255,255,.9)' }}>
                                ⭐ Destacado
                            </span>
                        </div>
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: '280px', padding: '48px 44px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                            <span style={{ fontSize: '.72rem', fontWeight: '700', color, letterSpacing: '.12em', textTransform: 'uppercase' }}>Artículo destacado</span>
                            <div style={{ height: '2px', width: '32px', background: color, borderRadius: '2px' }} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.4rem,2.5vw,2rem)', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.18', letterSpacing: '-.025em', marginBottom: '16px' }}>{post.titulo}</h2>
                        {post.subtitulo && <p style={{ fontSize: '.97rem', color: isDark ? '#cbd5e1' : '#475569', lineHeight: '1.85', marginBottom: '28px' }}>{post.subtitulo}</p>}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', paddingTop: '20px', borderTop: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: `linear-gradient(135deg,${color}cc,${color}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.78rem', fontWeight: '900', color: '#fff', flexShrink: 0 }}>
                                    {initials(post.nombre_autor || 'Au')}
                                </div>
                                <div>
                                    <div style={{ fontSize: '.85rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a' }}>{post.nombre_autor}</div>
                                    <div style={{ fontSize: '.72rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>{fmtDate(post.fecha_publicacion)}</div>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: '24px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: `linear-gradient(135deg,${color},${color}cc)`, color: '#fff', borderRadius: '12px', padding: '11px 24px', fontWeight: '700', fontSize: '.88rem', boxShadow: `0 4px 16px ${color}35`, transition: 'all .2s', transform: hov ? 'translateX(4px)' : 'translateX(0)' }}>
                                Leer artículo completo →
                            </span>
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Post Card ──
function PostCard({ post, idx, onClick }: { post: Publicacion; idx: number; onClick: () => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.06);
    const [hov, setHov] = useState(false);
    const color  = postColor(post, idx);
    const grad   = postGrad(post, idx);
    const emoji  = postEmoji(post, idx);
    const imgSrc = post.url_imagen || post.imagen || null;

    return (
        <div ref={ref as React.RefObject<HTMLDivElement>}
            style={{
                transition: 'all .35s cubic-bezier(.16,1,.3,1)',
                transform: inView ? (hov ? 'translateY(-6px)' : 'translateY(0)') : 'translateY(28px)',
                opacity: inView ? 1 : 0,
                transitionDelay: `${(idx % 3) * .07}s`,
                display: 'flex',
                height: '100%',
                width: '100%',
            }}
        >
            <BorderGlow
                borderRadius={22}
                backgroundColor={isDark ? '#150d24' : '#ffffff'}
                onClick={onClick}
                onMouseEnter={() => setHov(true)}
                onMouseLeave={() => setHov(false)}
                style={{ cursor: 'pointer', height: '100%', width: '100%', display: 'flex' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                    {/* Cover */}
                    <div style={{ height: '180px', position: 'relative', overflow: 'hidden', flexShrink: 0, background: grad }}>
                        {/* Gradiente + emoji como fondo */}
                        <div style={{ position: 'absolute', inset: 0, background: grad, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 0 }}>
                            <span style={{ fontSize: '3.2rem', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,.15))' }}>{emoji}</span>
                        </div>
                        {/* Imagen real encima */}
                        {imgSrc && (
                            <img src={imgSrc} alt={post.titulo}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1, transition: 'transform .55s cubic-bezier(.16,1,.3,1)', transform: hov ? 'scale(1.06)' : 'scale(1)' }}
                                onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,.22) 0%,transparent 50%)', zIndex: 2 }} />
                        <div style={{ position: 'absolute', top: '12px', left: '12px', background: 'rgba(0,0,0,.35)', backdropFilter: 'blur(8px)', borderRadius: '7px', padding: '3px 11px', fontSize: '.68rem', fontWeight: '700', color: '#fff', letterSpacing: '.04em', zIndex: 3 }}>
                            {post.categoria?.nombre || 'General'}
                        </div>
                        {post.es_destacado && (
                            <div style={{ position: 'absolute', top: '12px', right: '12px', background: `${color}dd`, backdropFilter: 'blur(6px)', borderRadius: '7px', padding: '3px 11px', fontSize: '.68rem', fontWeight: '700', color: '#fff', zIndex: 3 }}>
                                ⭐ Destacado
                            </div>
                        )}
                    </div>

                    {/* Body */}
                    <div style={{ padding: '22px 22px 24px', display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                        <h3 style={{ fontSize: '1.02rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.35', letterSpacing: '-.01em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {post.titulo}
                        </h3>
                        {post.subtitulo && (
                            <p style={{ fontSize: '.83rem', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.75', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
                                {post.subtitulo}
                            </p>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '14px', borderTop: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f8fafc', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: `linear-gradient(135deg,${color}cc,${color}66)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.65rem', fontWeight: '900', color: '#fff', flexShrink: 0 }}>
                                    {initials(post.nombre_autor || 'Au')}
                                </div>
                                <div>
                                    <div style={{ fontSize: '.78rem', fontWeight: '700', color: isDark ? '#e2e8f0' : '#374151' }}>{post.nombre_autor}</div>
                                    <div style={{ fontSize: '.68rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>{fmtDate(post.fecha_publicacion)}</div>
                                </div>
                            </div>
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: hov ? grad : (isDark ? `${color}25` : `${color}12`), border: `1.5px solid ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.75rem', color: hov ? '#fff' : (isDark ? '#FFD97C' : color), transition: 'all .25s', transform: hov ? 'rotate(45deg)' : 'rotate(0deg)', flexShrink: 0 }}>
                                →
                            </div>
                        </div>
                    </div>
                </div>
            </BorderGlow>
        </div>
    );
}

// ── Newsletter ──
function NewsletterBanner() {
    const [ref, inView] = useInView(0.15);
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>} className="newsletter-banner"
            style={{ borderRadius:'28px', overflow:'hidden', background:'linear-gradient(135deg,#230630,#431057,#61257B)', padding:'clamp(32px,6vw,56px) clamp(20px,5vw,48px)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'28px', opacity:inView?1:0, transform:inView?'translateY(0)':'translateY(32px)', transition:'all .75s cubic-bezier(.16,1,.3,1)', position:'relative', margin:'64px 0', boxShadow:'0 20px 48px rgba(97,37,123,0.25)' }}>
            <div style={{ position:'absolute', top:'-60px', left:'30%', width:'240px', height:'240px', background:'rgba(193,52,139,0.15)', borderRadius:'50%' }}/>
            <div style={{ position:'absolute', bottom:'-50px', right:'10%', width:'180px', height:'180px', background:'rgba(255,217,122,0.1)', borderRadius:'50%' }}/>
            <div style={{ position:'relative', flex:'1 1 280px', minWidth:0 }}>
                <div style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:'rgba(255,217,122,0.15)', border:'1.5px solid rgba(255,217,122,0.3)', borderRadius:'100px', padding:'5px 14px', marginBottom:'16px' }}>
                    <span style={{ fontSize:'.72rem', fontWeight:'800', color:'#FFD97C', letterSpacing:'.08em', textTransform:'uppercase' }}>Newsletter semanal</span>
                </div>
                <h3 style={{ fontSize:'clamp(1.2rem,3.5vw,2rem)', fontWeight:'900', color:'#fff', lineHeight:'1.15', letterSpacing:'-.02em', marginBottom:'12px' }}>El ecosistema boliviano,<br/>en tu bandeja cada semana</h3>
                <p style={{ fontSize:'.9rem', color:'rgba(255,255,255,.8)', lineHeight:'1.7', textAlign:'justify' }}>Artículos, recursos, oportunidades y eventos de Co Marca. Solo lo que importa, sin spam.</p>
            </div>
            <div style={{ position:'relative', flex:'1 1 280px', minWidth:'260px', maxWidth:'380px' }}>
                {!sent ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                        <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="tu@email.com"
                            style={{ width:'100%', padding:'14px 18px', borderRadius:'12px', border:'1.5px solid rgba(255,255,255,.2)', background:'rgba(255,255,255,.1)', color:'#fff', fontSize:'.93rem', outline:'none', fontFamily:"'DM Sans',sans-serif" }}/>
                        <button onClick={()=>{ if(email) setSent(true); }}
                            style={{ padding:'14px', borderRadius:'12px', border:'none', background:'linear-gradient(135deg,#61257B,#C1348B)', color:'#fff', fontWeight:'800', fontSize:'.93rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif", boxShadow:'0 6px 24px rgba(193,52,139,0.4)', transition:'all .2s' }}
                            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';}}
                            onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';}}>
                            Suscribirme gratis →
                        </button>
                        <p style={{ fontSize:'.72rem', color:'rgba(255,255,255,.35)', textAlign:'center' }}>+1,200 suscriptores. Cancela cuando quieras.</p>
                    </div>
                ) : (
                    <div style={{ textAlign:'center', padding:'24px', background:'rgba(255,255,255,.07)', borderRadius:'16px', border:'1.5px solid rgba(255,255,255,.15)' }}>
                        <div style={{ fontSize:'3rem', marginBottom:'12px' }}>🎉</div>
                        <div style={{ fontWeight:'800', color:'#fff', fontSize:'1.1rem', marginBottom:'6px' }}>¡Bienvenido/a!</div>
                        <div style={{ fontSize:'.85rem', color:'rgba(255,255,255,.65)' }}>Revisa tu correo — te enviaremos el primer número esta semana.</div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Category Filter ──
function CategoryFilter({ categorias, active, onChange }: { categorias: string[]; active: string; onChange: (c: string) => void }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const allCats = ['Todos', ...categorias];
    const catColor = (c: string, i: number) => c==='Todos' ? (isDark ? '#cbd5e1' : '#64748b') : paletteColors[(i-1) % paletteColors.length];
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>}
            style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'36px', opacity:inView?1:0, transform:inView?'translateY(0)':'translateY(16px)', transition:'all .5s cubic-bezier(.16,1,.3,1)' }}>
            {allCats.map((cat,i) => {
                const isActive = active===cat, c = catColor(cat,i);
                return (
                    <button key={cat} onClick={()=>onChange(cat)}
                        style={{
                            padding:'8px 18px', borderRadius:'100px',
                            background: isActive ? c : (isDark ? '#150d24' : '#fff'),
                            color: isActive ? '#fff' : (isDark ? '#e2e8f0' : c),
                            fontWeight:'700', fontSize:'.82rem', cursor:'pointer', fontFamily:"'DM Sans',sans-serif",
                            boxShadow: isActive ? `0 4px 16px ${c}35` : '0 1px 8px rgba(0,0,0,.06)',
                            border: isActive ? `1.5px solid ${c}` : (isDark ? '1.5px solid rgba(193,52,139,0.3)' : `1.5px solid ${c}25`),
                            transition:'all .22s cubic-bezier(.16,1,.3,1)',
                            transform: isActive ? 'translateY(-1px)' : 'translateY(0)'
                        }}
                        onMouseEnter={e=>{ if(!isActive){e.currentTarget.style.background=isDark ? 'rgba(193,52,139,0.2)' : `${c}12`; e.currentTarget.style.transform='translateY(-2px)';} }}
                        onMouseLeave={e=>{ if(!isActive){e.currentTarget.style.background=isDark ? '#150d24' : '#fff'; e.currentTarget.style.transform='translateY(0)';} }}>
                        {cat}
                    </button>
                );
            })}
        </div>
    );
}

// ── Section Label ──
function SectionLabel({ title, subtitle, color }: { title: string; subtitle: string; color: string }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    return (
        <div ref={ref as React.RefObject<HTMLDivElement>}
            style={{ marginBottom:'32px', opacity:inView?1:0, transform:inView?'translateY(0)':'translateY(18px)', transition:'all .6s cubic-bezier(.16,1,.3,1)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'6px' }}>
                <span style={{ fontSize:'.72rem', fontWeight:'700', letterSpacing:'.14em', textTransform:'uppercase', color }}>{subtitle}</span>
                <div style={{ height:'2px', width:'40px', borderRadius:'2px', background:color }}/>
            </div>
            <h2 style={{ fontSize:'clamp(1.4rem,2.5vw,1.9rem)', fontWeight:'800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing:'-.02em' }}>{title}</h2>
        </div>
    );
}

// ── Hero ──
function HeroSection({ total }: { total: number }) {
    const { isDark } = useTheme();
    const [ref, inView] = useInView(0.1);
    const [forced, setForced] = useState(false);
    useEffect(() => { const t = setTimeout(()=>setForced(true),100); return ()=>clearTimeout(t); }, []);
    const visible = inView || forced;
    return (
        <section style={{
            background: isDark
                ? 'linear-gradient(135deg, #0b0614 0%, #150a26 55%, #200d36 100%)'
                : 'linear-gradient(135deg,#fff 0%,#faf5fc 55%,#f5e8fa 100%)',
            padding:'clamp(100px,14vw,140px) 32px clamp(60px,8vw,80px)',
            position:'relative', overflow:'hidden'
        }}>
            <div className="blob-blog" style={{ position:'absolute', top:'-80px', right:'-80px', width:'420px', height:'420px', background: isDark ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))' : 'linear-gradient(135deg, rgba(97,37,123,0.14), rgba(193,52,139,0.1))', pointerEvents:'none' }}/>
            {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
            <HexagonCornerDecoration
                variant="horizontal-degrade"
                direction="left-to-right"
                opacity={isDark ? 0.70 : 0.50}
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <div ref={ref as React.RefObject<HTMLDivElement>} style={{ maxWidth:'1200px', margin:'0 auto', textAlign:'center' }}>
                <h1 className={`fade-up-blog d1 ${visible?'in':''}`} style={{ fontSize:'clamp(2.4rem,5vw,3.8rem)', fontWeight:'900', color: isDark ? '#ffffff' : '#0f172a', lineHeight:'1.08', letterSpacing:'-.025em', marginBottom:'12px' }}>
                    Ideas que <span style={{ background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>mueven Bolivia</span>
                </h1>
                <h2 className={`fade-up-blog d1 ${visible?'in':''}`} style={{ fontSize:'clamp(1.1rem,2vw,1.4rem)', fontWeight:'600', color: isDark ? '#cbd5e1' : '#475569', letterSpacing:'-.01em', marginBottom:'20px' }}>
                    El blog del ecosistema emprendedor boliviano
                </h2>
                <p className={`fade-up-blog d2 ${visible?'in':''}`} style={{ fontSize:'1.05rem', color: isDark ? '#94a3b8' : '#64748b', maxWidth:'580px', margin:'0 auto', lineHeight:'1.75', textAlign:'justify' }}>
                    Guías prácticas, análisis del ecosistema, historias de founders y todo lo que necesitas para construir un negocio sólido en Bolivia.
                </p>
                <div className={`fade-up-blog d3 ${visible?'in':''}`} style={{ display:'flex', justifyContent:'center', marginTop:'40px', flexWrap:'wrap' }}>
                    {[{ value:`${total}+`, label:'Artículos publicados', color:'#61257B' }, { value:'6', label:'Categorías temáticas', color:'#C1348B' }, { value:'1,200+', label:'Suscriptores', color:'#EA8406' }].map((s,i)=>(
                        <div key={s.label} style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'20px 36px', borderRight: i < 2 ? (isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9') : 'none' }}>
                            <div style={{ fontSize:'2.6rem', fontWeight:'900', color:s.color, lineHeight:1, letterSpacing:'-.03em' }}>{s.value}</div>
                            <div style={{ fontSize:'.82rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight:'500', marginTop:'6px' }}>{s.label}</div>
                        </div>
                    ))}
                </div>
                <div className={`fade-up-blog d3 ${visible?'in':''}`} style={{ marginTop:'32px', display:'flex', justifyContent:'center' }}>
                    <BrandStripes
                        variant="scroll-prompt"
                        size="md"
                        promptText="Explora nuestras publicaciones"
                    />
                </div>
            </div>
        </section>
    );
}

// ── Article Reader Modal ──
function ArticleReaderModal({ post, onClose, onOpenFull }: { post: Publicacion; onClose: () => void; onOpenFull: () => void }) {
    const { isDark } = useTheme();
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const imgSrc = post.url_imagen || post.imagen || null;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', overflowY: 'auto',
        }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: isDark ? '#150d24' : '#ffffff',
                    borderRadius: '24px', maxWidth: '820px', width: '100%',
                    maxHeight: '90vh', overflowY: 'auto',
                    border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
                    position: 'relative', display: 'flex', flexDirection: 'column',
                    fontFamily: "'DM Sans', sans-serif",
                }}
            >
                {/* Header Image / Gradient */}
                <div style={{
                    height: '240px', position: 'relative', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #420f54, #61257B)',
                    borderRadius: '24px 24px 0 0', flexShrink: 0,
                }}>
                    {imgSrc && (
                        <img src={imgSrc} alt={post.titulo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 70%)' }} />

                    <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px' }}>
                        <button
                            onClick={onOpenFull}
                            style={{
                                background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
                                borderRadius: '10px', padding: '6px 14px', fontSize: '0.78rem',
                                fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s',
                            }}
                        >
                            Página completa ↗
                        </button>
                        <button
                            onClick={onClose}
                            style={{
                                background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
                                width: '36px', height: '36px', borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', fontSize: '1.1rem',
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    <div style={{ position: 'absolute', bottom: '20px', left: '24px', right: '24px' }}>
                        <span style={{
                            background: 'rgba(255,217,122,0.25)', border: '1px solid rgba(255,217,122,0.4)',
                            borderRadius: '100px', padding: '4px 14px', fontSize: '0.75rem',
                            fontWeight: '800', color: '#FFD97C', textTransform: 'uppercase',
                        }}>
                            {post.categoria?.nombre || 'General'}
                        </span>
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '32px 36px 40px' }}>
                    <h1 style={{
                        fontSize: 'clamp(1.5rem, 3vw, 2.1rem)', fontWeight: '900',
                        color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.2', marginBottom: '14px',
                    }}>
                        {post.titulo}
                    </h1>

                    {post.subtitulo && (
                        <p style={{
                            fontSize: '1.05rem', color: isDark ? '#cbd5e1' : '#64748b',
                            lineHeight: '1.7', marginBottom: '22px', fontStyle: 'italic',
                        }}>
                            {post.subtitulo}
                        </p>
                    )}

                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 0', borderTop: isDark ? '1px solid rgba(193,52,139,0.25)' : '1px solid #f1f5f9',
                        borderBottom: isDark ? '1px solid rgba(193,52,139,0.25)' : '1px solid #f1f5f9',
                        marginBottom: '26px', flexWrap: 'wrap', gap: '12px',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                background: 'linear-gradient(135deg, #61257B, #C1348B)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', fontWeight: '800', fontSize: '0.85rem',
                            }}>
                                {initials(post.nombre_autor || 'CM')}
                            </div>
                            <div>
                                <div style={{ fontSize: '0.9rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a' }}>
                                    {post.nombre_autor}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>
                                    {fmtDate(post.fecha_publicacion)} · 5 min de lectura
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.origin + '/blog/' + post.slug);
                                    alert('¡Enlace del artículo copiado al portapapeles!');
                                }}
                                style={{
                                    background: isDark ? 'rgba(193,52,139,0.2)' : '#f3e8ff',
                                    border: isDark ? '1px solid rgba(193,52,139,0.4)' : '1px solid #d8b4fe',
                                    color: isDark ? '#f472b6' : '#61257B',
                                    borderRadius: '10px', padding: '8px 16px', fontSize: '0.82rem',
                                    fontWeight: '700', cursor: 'pointer',
                                }}
                            >
                                Compartir ⎘
                            </button>
                        </div>
                    </div>

                    {/* Article Body */}
                    <div
                        style={{
                            fontSize: '1rem', lineHeight: '1.85', color: isDark ? '#e2e8f0' : '#334155',
                        }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.contenido) }}
                    />
                </div>
            </div>
        </div>
    );
}

// ── Main ──
const Blog: React.FC = () => {
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [publicaciones, setPublicaciones] = useState<Publicacion[]>([]);
    const [destacado, setDestacado]         = useState<Publicacion|null>(null);
    const [loading, setLoading]             = useState(true);
    const [activeCategory, setActiveCategory] = useState('Todos');
    const [searchQuery, setSearchQuery]       = useState('');
    const [selectedPost, setSelectedPost]     = useState<Publicacion | null>(null);

    useEffect(() => {
        Promise.all([
            fetchPublicaciones({ estado:'publicado' }).catch(() => []),
            fetchDestacados().catch(() => []),
        ]).then(([pubs, dests]) => {
            const finalPubs = (pubs && pubs.length > 0) ? pubs : SAMPLE_BLOG_POSTS;
            const dest = (dests && dests.length > 0) ? dests[0] : (finalPubs.find(p => p.es_destacado) || finalPubs[0] || null);
            setDestacado(dest);
            setPublicaciones(finalPubs);
        }).catch((err) => {
            /* Browser diagnostics omitted. */
            setPublicaciones(SAMPLE_BLOG_POSTS);
            setDestacado(SAMPLE_BLOG_POSTS.find(p => p.es_destacado) || SAMPLE_BLOG_POSTS[0]);
        }).finally(() => setLoading(false));
    }, []);

    // "Todos los artículos" incluye TODOS (incluso el destacado)
    const categorias = Array.from(new Set(publicaciones.map(p=>p.categoria?.nombre).filter(Boolean))) as string[];
    const filtered   = activeCategory==='Todos'
        ? publicaciones
        : publicaciones.filter(p=>p.categoria?.nombre===activeCategory);

    const goTo = (p: Publicacion) => {
        setSelectedPost(p);
    };

    return (
        <div style={{ fontFamily:"'DM Sans',sans-serif", background: isDark ? '#0b0614' : '#f8fafc', minHeight:'100vh', transition: 'background-color 0.3s ease' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                *{box-sizing:border-box;margin:0;padding:0;}
                @keyframes blobFloat{0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;}}
                @keyframes shimmer{0%{background-position:200% 0;}100%{background-position:-200% 0;}}
                @keyframes spin{to{transform:rotate(360deg);}}
                .blob-blog{animation:blobFloat 9s ease-in-out infinite;}
                .fade-up-blog{opacity:0;transform:translateY(28px);transition:opacity .75s ease,transform .75s cubic-bezier(.16,1,.3,1);}
                .fade-up-blog.in{opacity:1;transform:translateY(0);}
                .fade-up-blog.d1{transition-delay:.1s;}
                .fade-up-blog.d2{transition-delay:.2s;}
                .fade-up-blog.d3{transition-delay:.3s;}
                input::placeholder{color:rgba(255,255,255,.35);}
                @media(max-width:900px){.featured-post{flex-direction:column!important;}.featured-post>div:first-child{flex:none!important;min-height:240px!important;}}
                @media(max-width:600px){.newsletter-banner{flex-direction:column!important;}.newsletter-banner>div{flex:none!important;width:100%!important;max-width:100%!important;min-width:0!important;}}
            `}</style>

            <HeroSection total={publicaciones.length || 50}/>

            <section style={{ padding:'60px 32px 100px', maxWidth:'1200px', margin:'0 auto' }}>

                {/* Artículo destacado */}
                {!loading && destacado && (
                    <>
                        <SectionLabel title="Artículo destacado" subtitle="No te lo pierdas" color={colors.primary}/>
                        <FeaturedPost post={destacado} idx={0} onClick={()=>goTo(destacado)}/>
                    </>
                )}
                {loading && (
                    <div style={{ borderRadius:'28px', border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9', background: isDark ? '#150d24' : '#fff', minHeight:'380px', marginBottom:'28px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <div style={{ width:'40px', height:'40px', border:`3px solid ${colors.primary}`, borderTopColor:'transparent', borderRadius:'50%', animation:'spin .7s linear infinite' }}/>
                    </div>
                )}

                <NewsletterBanner/>

                {/* Todos los artículos */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
                    <SectionLabel title="Todos los artículos" subtitle="Explora el blog" color={colors.secondary}/>
                    {/* Search bar */}
                    <div style={{
                        position: 'relative', width: '100%', maxWidth: '340px',
                    }}>
                        <input
                            type="text"
                            placeholder="Buscar por título, autor..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '11px 18px 11px 40px', borderRadius: '100px',
                                border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e2e8f0',
                                background: isDark ? '#150d24' : '#fff',
                                color: isDark ? '#ffffff' : '#0f172a', fontSize: '0.88rem',
                                outline: 'none', fontFamily: "'DM Sans', sans-serif",
                            }}
                        />
                        <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '1rem', opacity: 0.6 }}>
                            🔍
                        </span>
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                style={{
                                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
                                    fontSize: '0.85rem', fontWeight: 'bold',
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>

                <CategoryFilter categorias={categorias} active={activeCategory} onChange={setActiveCategory}/>

                {loading ? (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:'22px' }}>
                        {[1,2,3].map(i=>(
                            <div key={i} style={{ background: isDark ? '#150d24' : '#fff', borderRadius:'22px', overflow:'hidden', border: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9' }}>
                                <div style={{ height:'180px', background: isDark ? 'linear-gradient(90deg,#1f1333 0%,#2e1548 50%,#1f1333 100%)' : 'linear-gradient(90deg,#f1f5f9 0%,#e2e8f0 50%,#f1f5f9 100%)', backgroundSize:'200% 100%', animation:'shimmer 1.4s infinite' }}/>
                                <div style={{ padding:'22px' }}><div style={{ height:'16px', background: isDark ? '#22113a' : '#f1f5f9', borderRadius:'8px', marginBottom:'10px' }}/><div style={{ height:'12px', background: isDark ? '#1a0d2e' : '#f8fafc', borderRadius:'8px', width:'70%' }}/></div>
                            </div>
                        ))}
                    </div>
                ) : filtered.length>0 ? (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:'22px' }}>
                        {filtered.map((post,i)=><PostCard key={post.id} post={post} idx={i} onClick={()=>goTo(post)}/>)}
                    </div>
                ) : (
                    <div style={{ textAlign:'center', padding:'60px 20px', color: isDark ? '#94a3b8' : '#94a3b8' }}>
                        <div style={{ fontSize:'3rem', marginBottom:'12px' }}>✍️</div>
                        <div style={{ fontWeight:'700', fontSize:'1.1rem', color: isDark ? '#ffffff' : '#64748b', marginBottom:'6px' }}>
                            {searchQuery ? `No se encontraron resultados para "${searchQuery}"` : activeCategory==='Todos'?'No hay artículos publicados aún':`Pronto habrá artículos de ${activeCategory}`}
                        </div>
                        <div style={{ fontSize:'.88rem' }}>Intenta con otro término o explora otras categorías.</div>
                    </div>
                )}
            </section>

            {/* Modal de Lectura Completo */}
            {selectedPost && (
                <ArticleReaderModal
                    post={selectedPost}
                    onClose={() => setSelectedPost(null)}
                    onOpenFull={() => navigate(`/blog/${selectedPost.slug}`)}
                />
            )}
        </div>
    );
};

export default Blog;